import { useEffect, useRef, useState } from "react";
import { ErrorMessage, Formik } from "formik";
import {
  programTypes,
  DEBOUNCE_DELAY,
  initialMetaForTable,
} from "constants/common";
import { referral_status } from "constants/common";
import SelectComponent from "components/common/select";
import AsyncSelect from "components/common/select/AsyncSelect";
import { getCounties, getHealthPlans } from "containers/policies/api";
import debounce from "lodash/debounce";
import { useDebouncedHealthPlansDataCallback } from "utils/debouncedHelper";
import { formatLocation } from "utils/common";

const useDebouncedFetchCounties = () => {
  const debouncedFetch = useRef(
    debounce(async (inputValue, callback) => {
      try {
        const response = await getCounties({ search: inputValue });
        const formattedOptions =
          response?.counties?.map((el) => ({
            value: el.id,
            label: formatLocation(el.name),
          })) || [];

        callback(formattedOptions);
      } catch (error) {
        console.error("Failed to fetch counties:", error);
        callback([]);
      }
    }, DEBOUNCE_DELAY)
  ).current;

  return debouncedFetch;
};

const FilterReferrals = ({ handleSubmittion, handleClose, currentFilters }) => {
  const [countiesList, setCountiesList] = useState([]);
  const debouncedFetchCountiesData = useDebouncedFetchCounties();
  const [healthPlansList, setHealthPlansList] = useState([]);

  const fetchHealthPlan = async (searchString) => {
    const result = await getHealthPlans({
      ...initialMetaForTable,
      search: searchString,
    });

    if (result["health_plans"]) {
      const data = result?.health_plans;
      setHealthPlansList(data);
    }
  };

  useEffect(() => {
    fetchHealthPlan();
  }, []);

  const debouncedHealthPlansDataCallback = useDebouncedHealthPlansDataCallback();

  useEffect(() => {
    (async () => {
      try {
        const result = await getCounties({ ...initialMetaForTable, page: 1 });
        if (result?.counties) {
          setCountiesList(
            result.counties.map((el) => ({
              value: el.id,
              label: formatLocation(el.name),
            }))
          );
        }
      } catch (error) {
        console.error("Failed to load initial counties:", error);
      }
    })();
  }, []);

  return (
    <Formik
      initialValues={{
        county: currentFilters.county || [],
        referral_programe: currentFilters.referral_programe || [],
        health_plan_id: currentFilters.health_plan_id || [],
        referral_status: currentFilters.referral_status || [],
      }}
      onSubmit={(values, { setSubmitting }) => {
        handleSubmittion(values, setSubmitting);
      }}
    >
      {({ values, handleSubmit, setFieldValue, isSubmitting }) => (
        <div className="question-modal">
          <hr className="custom-modal-line mt-0" />
          <form className="form-main" onSubmit={handleSubmit}>
            <div className="input-flex row">
              <div className="col-sm-12">
                <SelectComponent
                  label="Referral Program"
                  name="referral_programe"
                  options={programTypes}
                  selectedValue={values.referral_programe}
                  isMulti
                  handleChange={(selected) =>
                    setFieldValue("referral_programe", selected)
                  }
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="referral_programe"
                    />
                  }
                />
              </div>
            </div>
            <div className="input-flex row">
              <div className="col-sm-12">
                <AsyncSelect
                  label="County"
                  isMulti
                  options={countiesList}
                  dataCallback={(inputValue) =>
                    new Promise((resolve) => {
                      debouncedFetchCountiesData(inputValue, resolve);
                    })
                  }
                  selectedValue={values.county}
                  placeholder="Select county"
                  handleChange={(selected) => {
                    setFieldValue("county", selected);
                  }}
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="county"
                    />
                  }
                />
              </div>
            </div>
            <div className="input-flex row">
              <div className="col-sm-12">
                <AsyncSelect
                  label="Health Plan"
                  options={healthPlansList.map((el) => ({
                    value: el.id,
                    label: el.name,
                  }))}
                  loadOptions={(inputValue) =>
                    new Promise((resolve) => {
                      debouncedHealthPlansDataCallback(inputValue, resolve);
                    })
                  }
                  selectedValue={values.health_plan_id}
                  placeholder="Select Health Plan "
                  handleChange={(obj) => {
                    setFieldValue("health_plan_id", obj.value);
                  }}
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="health_plan_id"
                    />
                  }
                />
              </div>
              <div className="col-sm-12">
                <SelectComponent
                  label="Referral Status"
                  name="referral_status"
                  options={referral_status}
                  selectedValue={
                    values?.referral_status
                      ? referral_status.filter((option) =>
                          values?.referral_status.includes(option?.value)
                        )
                      : []
                  }
                  isMulti
                  handleChange={(selectedOptions) =>
                    setFieldValue(
                      "referral_status",
                      selectedOptions
                        ? selectedOptions.map((option) => option?.value)
                        : []
                    )
                  }
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="referral_status"
                    />
                  }
                />
              </div>
            </div>
            <hr className="custom-modal-line mt-4" />
            <div className="action gap-2">
              <button
                className="secondary-btn w-50"
                type="button"
                onClick={handleClose}
              >
                Cancel
              </button>
              {isSubmitting ? (
                <span className="primary-btn w-50">
                  <span
                    className="spinner-grow spinner-grow-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Filtering...
                </span>
              ) : (
                <input
                  className="primary-btn w-50"
                  type="submit"
                  name="submit"
                  value="Apply"
                />
              )}
            </div>
          </form>
        </div>
      )}
    </Formik>
  );
};

export default FilterReferrals;
